import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import {
  CreateDepartmentRequest,
  CreateDepartmentUserRequest,
  DeleteDepartmentRequest,
  DeleteDepartmentUserRequest,
  GetDepartmentListResponse,
  GetDepartmentsListV1DepartmentsGetParams,
  UpdateDepartmentRequest,
} from 'compliance-api';
import { FormikErrors, FormikValues } from 'formik';
import { api } from '@/modules/common/api/index.ts';
import { useGetCurrentBusinessId } from '@/modules/common/hooks/useGetCurrentBusinessId.ts';
import { queryClient } from '@/modules/common/query.ts';
import { withErrorHandling } from '@/modules/common/utils/error-utils.tsx';
import { USERS_LIST_QUERY_KEY } from '@/modules/admin-dashboard/queries/users.ts';

export const CURRENT_USER_MY_DEPARTMENTS_QUERY_KEY = 'vestra-compliance/my-departments';
export const CURRENT_QUERY_DEPARTMENT_QUERY_KEY = 'vestra-compliance/current-department';

export const useCurrentUserDepartmentsQuery = (
  payload: GetDepartmentsListV1DepartmentsGetParams,
  options?: { disabled?: boolean; cacheKey?: string },
) => {
  const { disabled = false, cacheKey } = options || {};
  const { page } = payload;
  const businessId = useGetCurrentBusinessId();

  return useInfiniteQuery<GetDepartmentListResponse>({
    queryKey: [
      CURRENT_USER_MY_DEPARTMENTS_QUERY_KEY,
      JSON.stringify(payload),
      cacheKey,
      businessId,
    ],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async ({ pageParam = {} }: { pageParam?: { page?: number; search?: string } }) => {
      const response = await api.v1.getDepartmentsListV1DepartmentsGet({
        ...payload,
        page: typeof pageParam === 'number' && pageParam ? pageParam : pageParam?.page || page,
        filter_by_name: pageParam?.search || payload.filter_by_name,
      });
      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    getNextPageParam: ({ total, page, page_size }) => {
      const totalPage = Math.ceil(total / page_size);
      const hasMore = totalPage > page;
      if (hasMore) {
        return page + 1;
      }
    },
    enabled: !disabled,
  });
};

export const useUpdateDepartmentMutation = ({
  setErrors,
}: {
  setErrors: (errors: FormikErrors<FormikValues>) => void;
}) => {
  return useMutation({
    mutationFn: async (payload: UpdateDepartmentRequest) => {
      const response = await withErrorHandling(api.v1.updateDepartmentV1DepartmentsPatch(payload), {
        loading: 'Loading...',
        success: 'The department has been successfully updated',
        setErrors,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([CURRENT_USER_MY_DEPARTMENTS_QUERY_KEY]);
    },
  });
};

export const useDeleteDepartmentMutation = ({
  setErrors,
}: {
  setErrors?: (errors: FormikErrors<FormikValues>) => void;
}) => {
  return useMutation({
    mutationFn: async (payload: DeleteDepartmentRequest) => {
      const response = await withErrorHandling(
        api.v1.removeDepartmentV1DepartmentsDelete(payload),
        {
          loading: 'Loading...',
          success: 'The department has been successfully removed',
          setErrors,
        },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([CURRENT_USER_MY_DEPARTMENTS_QUERY_KEY]);
    },
  });
};

export const useCreateDepartmentMutation = ({
  setErrors,
}: {
  setErrors: (errors: FormikErrors<FormikValues>) => void;
}) => {
  return useMutation({
    mutationFn: async (payload: CreateDepartmentRequest) => {
      const response = await withErrorHandling(api.v1.createDepartmentV1DepartmentsPost(payload), {
        loading: 'Loading...',
        success: 'The department has been successfully created',
        setErrors,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([CURRENT_USER_MY_DEPARTMENTS_QUERY_KEY]);
    },
  });
};

export const useGetUsersFromDepartmentQuery = (departmentId: number, disabled = false) => {
  return useQuery({
    queryKey: [CURRENT_QUERY_DEPARTMENT_QUERY_KEY, departmentId],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response = await api.v1.getUsersByDepartmentV1DepartmentsUsersDepartmentIdGet({
        departmentId: departmentId,
      });
      return response.data;
    },
    enabled: !disabled,
  });
};

export const useAddUsersToDepartmentMutation = ({
  setErrors,
}: {
  setErrors: (errors: FormikErrors<FormikValues>) => void;
}) => {
  return useMutation({
    mutationFn: async (payload: CreateDepartmentUserRequest) => {
      const response = await withErrorHandling(
        api.v1.addUsersToDepartmentV1DepartmentsUsersPost(payload),
        {
          loading: 'Loading...',
          success: 'The department has been successfully updated',
          setErrors,
        },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([CURRENT_USER_MY_DEPARTMENTS_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_QUERY_DEPARTMENT_QUERY_KEY]);
      queryClient.invalidateQueries([USERS_LIST_QUERY_KEY]);
    },
  });
};

export const useDeleteUsersFromDepartmentMutation = ({
  setErrors,
}: {
  setErrors: (errors: FormikErrors<FormikValues>) => void;
}) => {
  return useMutation({
    mutationFn: async (payload: DeleteDepartmentUserRequest) => {
      const response = await withErrorHandling(
        api.v1.deleteUsersFromDepartmentV1DepartmentsUsersDelete(payload),
        {
          loading: 'Loading...',
          success: 'The department has been successfully updated',
          setErrors,
        },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([CURRENT_USER_MY_DEPARTMENTS_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_QUERY_DEPARTMENT_QUERY_KEY]);
    },
  });
};
